import React, { useState, useCallback, useEffect, useRef } from 'react';
import styles from './CommandCenterPopup.module.css';
import { GCC_API_BASE_URL } from '../../runtimeConfig';
import { CMD_CENTER_STORAGE_KEY } from '../../constants/cmdCenter';

/**
 * On submit, POSTs to /gcc-command-centre-v3 and renders the response in the
 * /cmdcenter React page (native dashboard) opened in a new tab.
 */
const CommandCenterPopup = ({ isOpen, onClose, onReopen }) => {
  const [websiteInputV3, setWebsiteInputV3] = useState('');
  const [isGccV3Loading, setIsGccV3Loading] = useState(false);
  const [gccV3Status, setGccV3Status] = useState(null);

  // Refs so async callbacks always read the latest values without stale closures
  const isLoadingRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  // Set true just before onReopen() so the reset effect skips clearing the success message
  const justCompletedRef = useRef(false);

  useEffect(() => { isLoadingRef.current = isGccV3Loading; }, [isGccV3Loading]);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

  // Reset form only when opening fresh — skip if mid-generation or just completed
  useEffect(() => {
    if (isOpen && !isLoadingRef.current) {
      if (justCompletedRef.current) {
        justCompletedRef.current = false;
        return;
      }
      setWebsiteInputV3('');
      setGccV3Status(null);
    }
  }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleGccCommandCentreV3 = useCallback(async (e) => {
    e.preventDefault();
    const website = websiteInputV3.trim();
    if (!website || isGccV3Loading) return;

    setIsGccV3Loading(true);
    setGccV3Status({ type: 'info', message: 'You can continue using the application. Once the Command Center is ready, it will automatically open in a new tab.' });

    try {
      const res = await fetch(`${GCC_API_BASE_URL}/gcc-command-centre-v3`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: website }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const data = await res.json();

      if (!data.isValid || data.didError || !data.dashboardData) {
        setGccV3Status({ type: 'error', message: 'The command returned no valid data. Please refine your query.' });
        return;
      }

      // Hand the response to the /cmdcenter React page (native dashboard — no HTML template).
      try {
        localStorage.setItem(CMD_CENTER_STORAGE_KEY, JSON.stringify({ command: website, response: data }));
      } catch (storageErr) {
        setGccV3Status({ type: 'error', message: 'Could not prepare the Command Center (browser storage is full or blocked). Please try again.' });
        return;
      }
      const tab = window.open('/cmdcenter', '_blank');
      if (!tab) {
        setGccV3Status({ type: 'error', message: 'Pop-up blocked. Please allow pop-ups for this site and try again.' });
        return;
      }
      setGccV3Status({ type: 'success', message: 'The Command Center has been successfully created and opened in a new tab.' });
      setWebsiteInputV3('');
    } catch (err) {
      setGccV3Status({ type: 'error', message: err.message || 'Failed to reach the GCC Command Centre V3 API.' });
    } finally {
      setIsGccV3Loading(false);
    }
  }, [websiteInputV3, isGccV3Loading]);

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="command-center-title"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title} id="command-center-title">Envision Your Command Center</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close Command Center popup"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleGccCommandCentreV3} className={styles.formRow}>
          <input
            type="text"
            value={websiteInputV3}
            onChange={(e) => setWebsiteInputV3(e.target.value)}
            placeholder="Input your Client's name"
            className={styles.input}
            disabled={isGccV3Loading}
            aria-label="GCC Command Centre V3 input"
            autoFocus
          />
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isGccV3Loading || !websiteInputV3.trim()}
          >
            {isGccV3Loading ? (
              <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeDasharray="31.4 31.4" />
              </svg>
            ) : 'Generate →'}
          </button>
        </form>

        {gccV3Status && (
          <p className={`${styles.status} ${gccV3Status.type === 'error' ? styles.statusError : gccV3Status.type === 'info' ? styles.statusInfo : styles.statusSuccess}`}>
            {gccV3Status.message}
          </p>
        )}

        {isGccV3Loading && (
          <div className={styles.okayRow}>
            <button
              type="button"
              className={styles.okayBtn}
              onClick={onClose}
            >
              Okay
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommandCenterPopup;
